import { Toaster } from "@/components/ui/toaster";
import { lazy, Suspense } from "react";
import ErrorBoundary from "@/components/ErrorBoundary";

// Lazy load Sonner - toast notifications don't need to be in initial bundle
const SonnerToaster = lazy(() => 
  import("@/components/ui/sonner").then(m => ({ default: m.Toaster }))
);
import { LazyTooltipProvider } from "@/components/LazyTooltipProvider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { isPageVisible } from "@/config/pageConfig";
import RootLayout from "./components/RootLayout";
import LoadingAnimation from "./components/LoadingAnimation";

// Eager import only for landing page
import Index from "./pages/Index";

// Lazy load all other pages for optimal code splitting
const MeetTheFounders = lazy(() => import("./pages/MeetTheFounders"));
const OurVision = lazy(() => import("./pages/OurVision"));
const Stories = lazy(() => import("./pages/Stories"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Waitlist = lazy(() => import("./pages/Waitlist"));
const WaitlistSuccess = lazy(() => import("./pages/WaitlistSuccess"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ForPartners = lazy(() => import("./pages/ForPartners"));
const ForInsurers = lazy(() => import("./pages/ForInsurers"));
const ForUtilities = lazy(() => import("./pages/ForUtilities"));
const ForRealEstateBrokers = lazy(() => import("./pages/ForRealEstateBrokers"));
const BookDemo = lazy(() => import("./pages/BookDemo"));
const HomeReportRoadmap = lazy(() => import("./pages/HomeReportRoadmap"));
const ProjectCostEstimates = lazy(() => import("./pages/ProjectCostEstimates"));

const RepairsAndImprovements = lazy(() => import("./pages/RepairsAndImprovements"));
const Repairs = lazy(() => import("./pages/Repairs"));
const MaintenanceChecklist = lazy(() => import("./pages/MaintenanceChecklist"));
const YourMonthlyBeat = lazy(() => import("./pages/YourMonthlyBeat"));
const AboutInspections = lazy(() => import("./pages/AboutInspections"));
const RRM = lazy(() => import("./pages/RRM"));
const TextConcierge = lazy(() => import("./pages/TextConcierge"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <LazyTooltipProvider>
        <Toaster />
        <Suspense fallback={null}>
          <SonnerToaster />
        </Suspense>
        
        <BrowserRouter>
          <RootLayout>
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><LoadingAnimation /></div>}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/meet-the-founders" element={<MeetTheFounders />} />
                <Route path="/our-vision" element={<OurVision />} />
                <Route path="/stories" element={<Stories />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/waitlist" element={<Waitlist />} />
                <Route path="/waitlist/success" element={<WaitlistSuccess />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/for-partners" element={<ForPartners />} />
                <Route path="/for-insurers" element={<ForInsurers />} />
                <Route path="/for-utilities" element={<ForUtilities />} />
                {isPageVisible("forRealEstateBrokers") ? (
                  <Route path="/for-real-estate-brokers" element={<ForRealEstateBrokers />} />
                ) : (
                  <Route path="/for-real-estate-brokers" element={<NotFound />} />
                )}
                <Route path="/book-demo" element={<BookDemo />} />
                <Route path="/home-report-roadmap" element={<HomeReportRoadmap />} />
                <Route path="/project-cost-estimates" element={<ProjectCostEstimates />} />
                <Route path="/replacement-predictions" element={<NotFound />} />
                <Route path="/home-improvement" element={<RepairsAndImprovements />} />
                <Route path="/repairs" element={<Repairs />} />
                <Route path="/repairs-and-improvements" element={<Navigate to="/home-improvement" replace />} />
                <Route path="/maintenance" element={<MaintenanceChecklist />} />
                <Route path="/maintenance-checklist" element={<Navigate to="/maintenance" replace />} />
                <Route path="/your-monthly-beat" element={<YourMonthlyBeat />} />
                <Route path="/about-inspections" element={<AboutInspections />} />
                <Route path="/rrm" element={<RRM />} />
                <Route path="/text-concierge" element={<TextConcierge />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </RootLayout>
        </BrowserRouter>
      </LazyTooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
